import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DELIVERY_REGISTER_URL, DELIVERY_EDIT_URL } from "../../../utils/urls";
import instance from "../../../services/services";
import DaumPostcodeEmbed from 'react-daum-postcode';
import PopupLoginError from "../../../components/Popup/LoginError";
import { useLanguage } from "../../../hooks/useLanguage";

function RegisterDeliveryAddr(){
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mde_seq = queryParams.get('mde_seq');
  const isEdit = !!mde_seq;
  const [isLoginError, setIsLoginError] = useState(false);
  
  // location.state에서 전달된 데이터 가져오기
  const stateData = location.state || {};
  
  // 배송지 정보 상태 초기화 (state에서 가져온 값 또는 기본값)
  const [deliveryInfo, setDeliveryInfo] = useState({
    mde_title: stateData.deliveryInfo?.mde_title || '',
    recipient_name: stateData.deliveryInfo?.recipient_name || '',
    recipient_phone: stateData.deliveryInfo?.recipient_phone || '',
    default_flag: stateData.deliveryInfo?.default_flag || 'N'
  });
  
  // 우편번호 관련 상태 초기화 (state에서 가져온 값 또는 기본값)
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    zonecode: stateData.deliveryAddress?.zonecode || '',
    roadAddress: stateData.deliveryAddress?.roadAddress || '',
    detailAddress: stateData.deliveryAddress?.detailAddress || ''
  });
  
  // 입력 필드 변경 핸들러
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setDeliveryInfo(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  // 기본 배송지 체크박스 핸들러
  const handleDefaultFlagChange = (e) => {
    setDeliveryInfo(prev => ({
      ...prev,
      default_flag: e.target.checked ? 'Y' : 'N'
    }));
  };
  
  // 우편번호 찾기 버튼 클릭 핸들러
  const handlePostcodeOpen = () => {
    setIsPostcodeOpen(true);
  };
  
  // 우편번호 선택 완료 핸들러
  const handlePostcodeComplete = (data) => {
    setDeliveryAddress({
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      detailAddress: ''
    });
    setIsPostcodeOpen(false);
  };
  
  // 우편번호 팝업 닫기 핸들러
  const handlePostcodeClose = () => {
    setIsPostcodeOpen(false);
  };
  
  // 상세 주소 변경 핸들러
  const handleDetailAddressChange = (e) => {
    setDeliveryAddress(prev => ({
      ...prev,
      detailAddress: e.target.value
    }));
  };

    // 팝업 확인 버튼 클릭 핸들러
    const handleConfirm = () => {
      if (isLoginError.action === 'navigate_inquiry') {
        navigate('/myPage/delivery'); // 커뮤니티 문의 목록 페이지로 이동
      }
      setIsLoginError(false); // 팝업 닫기
    };

  
  // 배송지 저장 핸들러 (등록 또는 수정)
  const handleSave = async () => {
    // 유효성 검사
    if (!deliveryInfo.mde_title) {
      setIsLoginError(t('delivery_address_name'));
      return;
    }
    
    if (!deliveryInfo.recipient_name) {
      setIsLoginError(t('delivery_address_name2'));
      return;
    }
    
    if (!deliveryInfo.recipient_phone) {
      setIsLoginError(t('delivery_address_name3'));
      return;
    }
    
    if (!deliveryAddress.zonecode || !deliveryAddress.roadAddress) {
      setIsLoginError(t('delivery_address_name4'));
      return;
    }
    
    try {
      const requestData = {
        mde_title: deliveryInfo.mde_title,
        recipient_name: deliveryInfo.recipient_name,
        recipient_phone: deliveryInfo.recipient_phone,
        recipient_zipcode: deliveryAddress.zonecode,
        recipient_address_street: deliveryAddress.roadAddress,
        recipient_address_detail: deliveryAddress.detailAddress,
        default_flag: deliveryInfo.default_flag
      };
      
      // 수정 모드일 경우 mde_seq 추가
      if (isEdit) {
        requestData.mde_seq = mde_seq;
      }
      
      // API URL 결정 (등록 또는 수정)
      const apiUrl = isEdit ? DELIVERY_EDIT_URL : DELIVERY_REGISTER_URL;
      
      const response = await instance.post(apiUrl, requestData);
      
      if (response.data.success) {
        setIsLoginError({
          message: isEdit ? t('delivery_address_modified') : t('delivery_address_registered'),
          action: 'navigate_inquiry'
        });
      } else {
        setIsLoginError({
          message: isEdit ? t('delivery_address_failed') : t('delivery_address_failed'),
          action: 'navigate_inquiry'
        });
      }
    } catch (error) {
      console.error(isEdit ? '배송지 수정 오류:' : '배송지 등록 오류:', error);
      setIsLoginError({
        message: isEdit ? '배송지 수정 중 오류가 발생했습니다.' : '배송지 등록 중 오류가 발생했습니다.',
        action: 'navigate_inquiry'
      });
    }
  };

  return(
    <div>
      <div className="my_container point_container mt_48">
        <div className="order_title">
          <h3>{isEdit ? t('delivery_address_modified2') : t('delivery_address_registered2')}</h3>
        </div>

        <ul className="default_info_table v2 delivery_addr_form">
          {/* 배송지명 */}
          <li className="info_item">
            <div className="label">
              <label htmlFor="mde_title">{t('delivery_address_name6')}</label>
            </div>
            <div className="form_info">
              <div className="info_detail default_input">
                <input
                  id="mde_title"
                  className="w-half"
                  placeholder={t('delivery_address_name5')}
                  type="text"
                  value={deliveryInfo.mde_title}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </li>

          {/* 성명 */}
          <li className="info_item">
            <div className="label">
              <label htmlFor="recipient_name">{t('name2')}</label>
            </div>
            <div className="form_info">
              <div className="info_detail default_input">
                <input
                  id="recipient_name"
                  className="w-half"
                  placeholder={t('delivery_address_name7')}
                  type="text"
                  value={deliveryInfo.recipient_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </li>

          {/* 핸드폰 번호 */}
          <li className="info_item">
            <div className="label">
              <label htmlFor="recipient_phone">{t('phone_number')}</label>
            </div>
            <div className="form_info">
              <div className="info_detail default_input">
                <input
                  id="recipient_phone"
                  className="w-half"
                  placeholder={t('delivery_address_name8')}
                  type="text"
                  value={deliveryInfo.recipient_phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </li>

          {/* 배송 주소 */}
          <li className="info_item btm_line">
            <div className="label">
              <span>{t('delivery_address')}</span>
            </div>
            <div className="form_info">
              <div className="info_detail default_input">
                <div className="address_input">
                  <div className="zip_code flex-center-4">
                    <input 
                      type="text" 
                      value={deliveryAddress.zonecode}
                      readOnly 
                    />
                    <button 
                      type="button" 
                      className="default_btn sm light_gray"
                      onClick={handlePostcodeOpen}
                    >
                      {t('postal_code_search')}
                    </button>
                  </div>
                  <input 
                    type="text" 
                    value={deliveryAddress.roadAddress}
                    readOnly 
                  />
                  <input 
                    type="text" 
                    placeholder={t('delivery_address_name9')} 
                    value={deliveryAddress.detailAddress}
                    onChange={handleDetailAddressChange}
                  />
                </div>
              </div>
            </div>
          </li>
        </ul>

        <div className="default_chk_box btm_btn_box">
          <input
            id="default_flag"
            type="checkbox"
            checked={deliveryInfo.default_flag === 'Y'}
            onChange={handleDefaultFlagChange}
          />
          <label htmlFor="default_flag">
            <span>{t('save_as_default_address')}</span>
          </label>
        </div>
      </div>

      <div className="submit_box bg">
        <Link 
          to="/myPage/delivery" 
          className="default_btn lg white"
        >
          {t('cancel')}
        </Link>
        <button
          className="default_btn lg black"
          type="button"
          onClick={handleSave}
        >
          {isEdit ? t('modify') : t('register')}
        </button>
      </div>

      {/* 우편번호 모달 */}
      {isPostcodeOpen && (
        <>
          <div 
            className="dimmed" 
            onClick={handlePostcodeClose}
            style={{
              position: 'fixed',
              width: '100vw',
              height: '100vh',
              top: '0',
              left: '0',
              background: 'rgba(0, 0, 0, 0.50)',
              zIndex: 101
            }}
          />
          <div className="default_popup">
            <div className="modal_content">
              <div className="modal_wrap">
                <div className="modal_header">
                  <button 
                    type="button" 
                    className="close_btn" 
                    onClick={handlePostcodeClose}
                  ></button>
                </div>
                <div className="modal_body">
                  <DaumPostcodeEmbed
                    onComplete={handlePostcodeComplete}
                    onClose={handlePostcodeClose}
                    style={{ height: '460px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <PopupLoginError isPopup={isLoginError} setIsPopup={setIsLoginError} onConfirm={handleConfirm}/>
    </div>
  );
}

export default RegisterDeliveryAddr;